import React, { memo, useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CallRecordingSettings from './call-recording-settings';
import { RootState } from '../../../../../../../../../../store/root-reducer';
import { PhoneNumberSettingCode } from '../../../../../../../../types/phone-numbers';
import {
  updatePhoneNumberSettingsRequest,
  getPhoneNumberDetailsRequest,
} from '../../../../../../../../extra-actions';
import { RequestStatus } from '../../../../../../../../../../shared/enums/request-status';
import { resetUpdatePhoneNumberSettingsRequest } from '../../../../../../../../dialer-slice';
import toaster, { Theme } from '../../../../../../../../../../shared/toaster';

const CallRecordingSettingsContainer: React.FC = () => {
  const dispatch = useDispatch();
  const activeNumberSettings = useSelector(
    (state: RootState) => state.dialer.activeNumberSettings,
  );
  const updatePhoneNumberSettingsRequestStatus = useSelector(
    (state: RootState) =>
      state.dialer.updatePhoneNumberSettingsRequest[
        PhoneNumberSettingCode.CALL_RECORDING_ENABLED
      ]?.status || RequestStatus.Ideal,
  );

  const callRecordingEnabled = useMemo(() => {
    if (!activeNumberSettings?.settings) {
      return null;
    }
    const setting = activeNumberSettings.settings.find(
      (s) => s.code === PhoneNumberSettingCode.CALL_RECORDING_ENABLED,
    );
    return setting?.value === 'true';
  }, [activeNumberSettings]);

  const transcriptionLanguage = useMemo(() => {
    if (!activeNumberSettings?.settings) {
      return 'en-US';
    }
    const setting = activeNumberSettings.settings.find(
      (s) => s.code === PhoneNumberSettingCode.TRANSCRIPTION_LANGUAGE,
    );
    return setting?.value || 'en-US';
  }, [activeNumberSettings]);

  const handleSave = useCallback(
    (enabled: boolean, language: string) => {
      if (!activeNumberSettings?.id) {
        return;
      }

      dispatch(
        updatePhoneNumberSettingsRequest({
          phoneNumberIds: [activeNumberSettings.id],
          settings: [
            {
              code: PhoneNumberSettingCode.CALL_RECORDING_ENABLED,
              value: enabled ? 'true' : 'false',
            },
            {
              code: PhoneNumberSettingCode.TRANSCRIPTION_LANGUAGE,
              value: language,
            },
          ],
        }),
      );
    },
    [dispatch, activeNumberSettings?.id],
  );

  useEffect(() => {
    if (
      updatePhoneNumberSettingsRequestStatus === RequestStatus.Succeeded &&
      activeNumberSettings?.id
    ) {
      dispatch(getPhoneNumberDetailsRequest(activeNumberSettings.id));
      dispatch(
        resetUpdatePhoneNumberSettingsRequest(
          PhoneNumberSettingCode.CALL_RECORDING_ENABLED,
        ),
      );
      toaster.success('Number Setting Updated Successfully', {
        theme: Theme.New,
      });
    }
  }, [
    updatePhoneNumberSettingsRequestStatus,
    activeNumberSettings?.id,
    dispatch,
  ]);

  return (
    <CallRecordingSettings
      isCallRecordingEnabled={callRecordingEnabled}
      transcriptionLanguage={transcriptionLanguage}
      onSave={handleSave}
      isLoading={
        updatePhoneNumberSettingsRequestStatus === RequestStatus.Pending
      }
    />
  );
};

export default memo(CallRecordingSettingsContainer);
